import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, ChevronDown, X } from "lucide-react";
import logoImage from "@assets/CaribbeanBreeze_logo_over_pictures_1785782527674.webp";

const navItems = [
  {
    label: "Services",
    href: "#",
    children: [
      { label: "Solar-Powered AC", href: "/solar-powered-ac" },
      { label: "AC & Heating Installation", href: "/ac-heating-installation" },
      { label: "AC & Heating Repair", href: "/ac-heating-repair" },
      { label: "Ductless Mini-Splits", href: "/ductless-mini-splits" },
      { label: "Commercial HVAC", href: "/commercial-hvac" },
      { label: "Maintenance Plans", href: "/maintenance-plans" },
    ],
  },
  { label: "Financing", href: "/financing" },
  { label: "Blog", href: "/hvac-resources" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setServicesOpen(false);
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm"
          : "bg-background/95 backdrop-blur-md border-b border-border"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-16">
          {/* Logo */}
          <Link href="/" data-testid="link-home">
            <div className="flex items-center gap-2 cursor-pointer">
              <img
                src={logoImage}
                alt="Caribbean Breeze HVAC — Indoor Comfort Air Conditioning & Heating"
                width={54}
                height={44}
                className="h-11 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label} className="relative">
                  <Button
                    variant="ghost"
                    className="gap-1"
                    onClick={() => setServicesOpen((v) => !v)}
                    onBlur={() => setTimeout(() => setServicesOpen(false), 150)}
                    data-testid="nav-services-dropdown"
                  >
                    {item.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                    />
                  </Button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-1 w-60 bg-background border border-border rounded-lg shadow-lg py-1 z-50">
                      {item.children.map((child) => (
                        <Link key={child.href} href={child.href}>
                          <span
                            className={`block px-4 py-2.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors ${
                              location === child.href ? "text-primary font-medium bg-accent/50" : ""
                            }`}
                          >
                            {child.label}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.href} href={item.href!}>
                  <Button
                    variant="ghost"
                    className={location === item.href ? "bg-accent" : ""}
                    data-testid={`link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {item.label}
                  </Button>
                </Link>
              )
            )}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a href="tel:6027770278" data-testid="header-phone">
              <Button className="gap-2 font-semibold">
                <Phone className="w-4 h-4" />
                (602) 777-0278
              </Button>
            </a>
          </div>

          {/* Mobile menu */}
          <div className="flex md:hidden items-center gap-2">
            <a href="tel:6027770278">
              <Button size="sm" variant="outline" className="gap-1.5 text-xs px-3">
                <Phone className="w-3.5 h-3.5" />
                Call
              </Button>
            </a>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="px-2" aria-label="Open menu">
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 pt-12">
                <nav className="flex flex-col gap-1">
                  {navItems.map((item) =>
                    item.children ? (
                      <div key={item.label}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 pt-4 pb-2">
                          Services
                        </p>
                        {item.children.map((child) => (
                          <Link key={child.href} href={child.href}>
                            <span
                              className={`flex items-center px-3 py-3 min-h-[44px] rounded-md text-sm cursor-pointer hover:bg-accent transition-colors ${
                                location === child.href ? "text-primary font-semibold bg-accent/60" : ""
                              }`}
                            >
                              {child.label}
                            </span>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={item.href} href={item.href!}>
                        <span
                          className={`flex items-center px-3 py-3 min-h-[44px] rounded-md text-sm cursor-pointer hover:bg-accent transition-colors mt-1 ${
                            location === item.href ? "text-primary font-semibold bg-accent/60" : ""
                          }`}
                        >
                          {item.label}
                        </span>
                      </Link>
                    )
                  )}
                  <div className="mt-6 px-3">
                    <a href="tel:6027770278" className="block">
                      <Button className="w-full gap-2 font-semibold">
                        <Phone className="w-4 h-4" />
                        (602) 777-0278
                      </Button>
                    </a>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
